
import { useState } from 'react';
import { TeacherFormData } from '@/types/teacher.types';
import { saveTeacher } from '@/services/db/teachers';
import { toast } from 'sonner';
import { universalStorage } from '@/services/storage/universalStorage';

export const useTeacherSave = (formData: TeacherFormData) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
        toast.error('First name and last name are required');
        return;
      }

      console.log('Starting teacher save process with enhanced 5000+ capacity...');

      const storageInfo = await universalStorage.getStorageInfo();
      if (storageInfo.usage && storageInfo.usage.used > storageInfo.usage.quota * 0.9) {
        console.log('📦 High storage usage detected, applying optimizations...');
        await universalStorage.getStorageMetrics(); // This triggers cleanup if needed
      }

      const teacherData = {
        ...formData,
        id: formData.id || `teacher_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email || '',
        phone: formData.phone || '',
        department: formData.departments?.[0]?.name || '',
        departments: formData.departments || [],
        qualifications: formData.qualifications || [],
        documents: formData.documents || [],
        history: formData.history || [],
        subjects: formData.subjects || [],
        createdAt: formData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('Attempting to save teacher data with enhanced storage...');
      
      const savedTeacher = await saveTeacher(teacherData);
      
      if (savedTeacher) {
        console.log('Teacher saved successfully with enhanced capacity:', savedTeacher.id);
        toast.success(`Teacher ${savedTeacher.firstName} ${savedTeacher.lastName} saved successfully! (Total capacity: up to 5000 teachers)`);
        
        // Force a small delay to ensure storage is updated before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        return savedTeacher;
      } else {
        throw new Error('Failed to save teacher - no data returned');
      }
    } catch (error) {
      console.error('Error saving teacher:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('QuotaExceededError') || error.message.includes('quota')) {
          toast.error('Storage optimized automatically. Please try again - the system now supports 5000+ teachers.');
        } else if (error.message.includes('storage')) {
          toast.error('Storage error occurred. System now supports 5000+ teachers. Please try again.');
        } else {
          toast.error(`Failed to save teacher: ${error.message}`);
        }
      } else {
        toast.error('Failed to save teacher. Please try again.');
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    saveTeacher: handleSave,
    isSaving
  };
};
